import { useRef, useState } from "react";
import { MAX_PHOTOS_PER_ITEM } from "@iltocat/shared";
import { isImage, preparePhoto, type PreparedPhoto } from "../lib/photos.js";
import { Icon } from "./Icon.js";

/**
 * Where a capture starts: photographs, before anything is typed.
 *
 * **Two buttons, not one, and the split is the whole point.** `capture="environment"`
 * on a file input opens the rear camera directly on a phone, which is the
 * one-tap path the brief asks for, but on a desktop it is meaningless, and on
 * some phones it also *removes* the option to pick something already in the
 * photo roll. So there is a camera button that goes straight to the camera and
 * a second that opens the library or the file picker, and the desktop gets a
 * drop zone over the whole thing.
 *
 * Each file is resized as it arrives rather than at save time, so the waiting
 * happens while somebody is still deciding what to type. By the time they press
 * Save, there is nothing left to do but send it.
 */

export interface PickedPhoto extends PreparedPhoto {
  /** Stable across re-renders, so React does not re-key a preview and flash it. */
  key: string;
}

export function PhotoPicker({
  photos,
  onChange,
  onError,
  busy = false,
}: {
  photos: PickedPhoto[];
  onChange: (photos: PickedPhoto[]) => void;
  onError: (reason: string) => void;
  busy?: boolean;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [working, setWorking] = useState(0);
  const [dragging, setDragging] = useState(false);

  const room = MAX_PHOTOS_PER_ITEM - photos.length;

  async function accept(files: FileList | null) {
    if (!files || files.length === 0) return;

    const wanted = [...files].filter(isImage).slice(0, Math.max(room, 0));
    if (wanted.length === 0) {
      onError(room <= 0 ? "That is as many photos as one item holds." : "Those were not photos.");
      return;
    }

    setWorking(wanted.length);
    const prepared: PickedPhoto[] = [];
    for (const file of wanted) {
      try {
        const photo = await preparePhoto(file);
        prepared.push({
          ...photo,
          key: `${file.name}-${file.lastModified}-${prepared.length}-${Date.now()}`,
        });
      } catch (problem) {
        onError(problem instanceof Error ? problem.message : "That photo could not be read.");
      }
      setWorking((count) => count - 1);
    }
    onChange([...photos, ...prepared]);
  }

  function drop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);
    void accept(event.dataTransfer.files);
  }

  function discard(key: string) {
    const going = photos.find((photo) => photo.key === key);
    if (going) URL.revokeObjectURL(going.previewUrl);
    onChange(photos.filter((photo) => photo.key !== key));
  }

  const buttons = (
    <div className="shots-buttons">
      <button
        type="button"
        className="button"
        disabled={busy || room <= 0}
        onClick={() => camera.current?.click()}
      >
        <Icon name="camera" size={18} />
        Take photo
      </button>
      <button
        type="button"
        className="button button-quiet"
        disabled={busy || room <= 0}
        onClick={() => library.current?.click()}
      >
        <Icon name="image" size={18} />
        Choose
      </button>
    </div>
  );

  return (
    <div
      className={`shots${dragging ? " shots-dragging" : ""}${photos.length === 0 ? " shots-empty" : ""}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        // Moving over a button inside is not leaving, and would flicker.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={drop}
    >
      {/*
        Both inputs are hidden and driven by the buttons, because a bare file
        input cannot be made to look like anything and this is the first thing
        anybody sees. The value is cleared after every pick so that choosing the
        same file twice in a row still fires a change.
      */}
      <input
        ref={camera}
        className="hidden-input"
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        tabIndex={-1}
        onChange={(event) => {
          void accept(event.target.files);
          event.target.value = "";
        }}
      />
      <input
        ref={library}
        className="hidden-input"
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        onChange={(event) => {
          void accept(event.target.files);
          event.target.value = "";
        }}
      />

      {photos.length === 0 ? (
        <div className="shots-invite">
          <span className="shots-icon">
            <Icon name="camera" size={28} />
          </span>
          <p className="shots-title">Photos first</p>
          <p className="shots-hint">
            A picture is enough to save. <span className="hide-sm">Drop them here, or:</span>
          </p>
          {buttons}
        </div>
      ) : (
        <>
          <ul className="shot-grid">
            {photos.map((photo, index) => (
              <li key={photo.key} className="shot">
                <img src={photo.previewUrl} alt={`Photo ${index + 1}`} />
                {index === 0 && <span className="shot-badge">Cover</span>}
                <button
                  type="button"
                  className="shot-remove"
                  aria-label={`Remove photo ${index + 1}`}
                  disabled={busy}
                  onClick={() => discard(photo.key)}
                >
                  <Icon name="x" size={14} />
                </button>
              </li>
            ))}
            {room > 0 && (
              <li>
                <button
                  type="button"
                  className="shot-add"
                  disabled={busy}
                  aria-label="Add another photo"
                  onClick={() => library.current?.click()}
                >
                  <Icon name="plus" size={22} />
                </button>
              </li>
            )}
          </ul>
          {buttons}
        </>
      )}

      {working > 0 && (
        <p className="shots-working" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          Preparing {working}…
        </p>
      )}
    </div>
  );
}
